import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
	component: RouteComponent
});

function RouteComponent() {
	const sections = [
		{
			id: "introduction",
			title: "1. INTRODUCTION",
			content: (
				<p>
					Intasida Limited ("Intasida," "Company," "we," "us," or "our") is committed to protecting the rights and freedoms of data subjects (customers, employees, and partners) and ensuring the safe and secure processing of their personal data in compliance with the Nigeria Data Protection Act 2023, the Central Bank of Nigeria (CBN) Guidelines on Data Protection for Financial Institutions, and other applicable financial regulatory frameworks.
					<br />
					<br />
					We process personal data to provide secure and efficient financial services, ensure regulatory compliance, prevent fraud, and enhance user experience. This policy outlines our data protection commitments and ensures our employees and partners understand the principles governing the use of personal data.
				</p>
			)
		},
		{
			id: "definitions",
			title: "2. DEFINITIONS",
			content: (
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong>Personal Data:</strong>
						{" "}
						Any information that identifies an individual, including name, phone number, email, financial details, account numbers, and transaction history.
					</li>
					<li>
						<strong>Special Category Data:</strong>
						{" "}
						Sensitive data including biometric data, health information, and criminal records, which require enhanced protection.
					</li>
					<li>
						<strong>Data Controller:</strong>
						{" "}
						Intasida Limited, determining the purpose and means of data processing.
					</li>
					<li>
						<strong>Data Processor:</strong>
						{" "}
						Any third party processing personal data on behalf of Intasida.
					</li>
					<li>
						<strong>Processing:</strong>
						{" "}
						Any operation performed on personal data, including collection, storage, transmission, and deletion.
					</li>
					<li>
						<strong>Supervisory Authority:</strong>
						{" "}
						The National Data Protection Commission (NDPC) and other relevant financial regulatory bodies.
					</li>
				</ul>
			)
		},
		{
			id: "scope",
			title: "3. SCOPE",
			content: (
				<p>
					This policy applies to all personal data processed by Intasida Limited, including data collected from customers, employees, vendors, and third-party service providers.
				</p>
			)
		},
		{
			id: "purpose",
			title: "4. PURPOSE",
			content: (
				<div>
					<p>Intasida processes personal data for:</p>
					<ul className="list-disc pl-5 space-y-1">
						<li>Marketing and customer engagement (with consent)</li>
						<li>Financial transactions, including payments and loan services</li>
						<li>Fraud detection and compliance with anti-money laundering (AML) regulations</li>
						<li>Customer support and account management</li>
						<li>Internal business operations and human resources management</li>
					</ul>
				</div>
			)
		},
		{
			id: "procedures",
			title: "5. OUR PROCEDURES",
			content: (
				<div>
					<h3 className="text-lg font-medium mt-2 mb-1">5.1 Lawful Bases for Processing</h3>
					<p>Processing of personal data shall be lawful, fair, and transparent. We rely on the following lawful bases:</p>
					<ul className="list-disc pl-5 space-y-1">
						<li>
							<strong>Consent:</strong>
							{" "}
							Where customers explicitly agree to data collection and processing.
						</li>
						<li>
							<strong>Contractual Necessity:</strong>
							{" "}
							When processing is essential to fulfill a financial service.
						</li>
						<li>
							<strong>Legal Obligation:</strong>
							{" "}
							To comply with regulatory requirements.
						</li>
						<li>
							<strong>Legitimate Interest:</strong>
							{" "}
							When processing is necessary for fraud prevention, security, and business operations.
						</li>
					</ul>

					<h3 className="text-lg font-medium mt-4 mb-1">5.2 Security Measures</h3>
					<p>Intasida implements robust security measures, including:</p>
					<ul className="list-disc pl-5 space-y-1">
						<li>Encryption of sensitive financial data.</li>
						<li>Multi-factor authentication (MFA) for customer accounts.</li>
						<li>Regular security audits and risk assessments.</li>
						<li>Employee training on data security and fraud prevention.</li>
					</ul>

					<h3 className="text-lg font-medium mt-4 mb-1">5.3 Data Minimization & Retention</h3>
					<p>We collect only necessary data and retain personal information for the duration required by law. Customer financial records are retained in line with the Nigerian Data Protection Act 2023 and other relevant regulations.</p>
				</div>
			)
		},
		{
			id: "responsibilities",
			title: "6. RESPONSIBILITIES OF INTASIDA",
			content: (
				<div>
					<p>Intasida shall ensure that personal data is:</p>
					<ul className="list-disc pl-5 space-y-1">
						<li>Processed in a fair, lawful and transparent manner.</li>
						<li>Collected for specified, explicit and legitimate purposes, and not to be further processed in a way incompatible with this purposes.</li>
						<li>Adequate, relevant and limited to the minimum necessary for the purposes for which the personal data was collected or further processed.</li>
						<li>Retained for not longer than is necessary to achieve the lawful bases for which the personal data was collected or further processed.</li>
						<li>Accurate, complete, not misleading, and, where necessary kept up to date having regard to the purposes for which the personal data is collected or is further processed.</li>
					</ul>
				</div>
			)
		},
		{
			id: "rights",
			title: "7. RIGHTS OF INDIVIDUALS",
			content: (
				<div>
					<p>Data subjects have the following rights:</p>
					<ul className="list-disc pl-5 space-y-1">
						<li>
							<strong>Right to Access:</strong>
							{" "}
							Request a copy of personal data processed by Intasida.
						</li>
						<li>
							<strong>Right to Rectification:</strong>
							{" "}
							Correct inaccurate personal data.
						</li>
						<li>
							<strong>Right to Erasure:</strong>
							{" "}
							Request deletion of data where no longer necessary.
						</li>
						<li>
							<strong>Right to Restriction:</strong>
							{" "}
							Limit processing in certain conditions.
						</li>
						<li>
							<strong>Right to Data Portability:</strong>
							{" "}
							Obtain and reuse data for personal purposes.
						</li>
						<li>
							<strong>Right to Object:</strong>
							{" "}
							Stop processing based on legitimate interests.
						</li>
					</ul>
				</div>
			)
		},
		{
			id: "third-party",
			title: "8. THIRD-PARTY PROCESSING",
			content: (
				<p>
					Intasida engages third-party service providers (e.g., payment processors, KYC verification services) under strict data processing agreements to ensure compliance with this policy and the Nigerian Data Protection Act 2023.
				</p>
			)
		},
		{
			id: "breaches",
			title: "9. REPORTING BREACHES",
			content: (
				<div>
					<p>In case of a data breach, Intasida will:</p>
					<ul className="list-disc pl-5 space-y-1">
						<li>Notify affected individuals and the NDPC within 72 hours.</li>
						<li>Investigate and mitigate risks associated with the breach.</li>
						<li>Take corrective actions to prevent future occurrences.</li>
					</ul>
				</div>
			)
		},
		{
			id: "compliance",
			title: "10. COMPLIANCE & AUDITS",
			content: (
				<div>
					<p>
						Intasida is committed to maintaining the highest standards of data protection and ensuring full compliance with the Nigerian Data Protection Act 2023. To this end, the company conducts periodic audits and assessments to evaluate its data processing activities, security measures, and overall adherence to regulatory requirements. These audits are designed to:
					</p>
					<ul className="list-disc pl-5 space-y-1">
						<li>Identify and mitigate potential risks associated with data handling and storage.</li>
						<li>Ensure that all data protection policies and procedures align with legal and industry standards.</li>
						<li>Assess the effectiveness of technical and organizational measures implemented to safeguard personal data.</li>
						<li>Verify compliance with data subject rights, including access, rectification, and erasure requests.</li>
						<li>Review third-party processors and service providers to confirm adherence to contractual and regulatory obligations.</li>
					</ul>
					<p className="mt-2">
						Any identified non-compliance issues are promptly addressed through corrective measures, and employees are regularly trained on best practices to uphold data protection principles. Intasida remains proactive in updating its policies and processes in response to evolving data protection laws and industry developments.
					</p>
				</div>
			)
		}
	];

	return (
		<div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
			<div className="max-w-4xl mx-auto">
				<div className="bg-white shadow overflow-hidden rounded-lg">
					<div className="px-4 py-8 sm:px-6 lg:px-8">
						<div className="text-center mb-8">
							<h1 className="text-3xl font-bold ">INTASIDA DATA PROTECTION / PRIVACY POLICY</h1>
							<p className="mt-2 text-gray-600">Last Updated: April 2025</p>
						</div>

						<Accordion type="single" collapsible className="space-y-4">
							{sections.map(section => (
								<AccordionItem key={section.id} value={section.id} className="border border-gray-200 rounded-md overflow-hidden">
									<AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left font-medium text-blue-700">
										{section.title}
									</AccordionTrigger>
									<AccordionContent className="px-4 py-4 bg-white text-gray-700">
										{section.content}
									</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>

						<div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
							<p>
								&copy;
								{new Date().getFullYear()}
								{" "}
								Intasida Limited. All rights reserved.
							</p>
							<p className="mt-1">For questions regarding this policy, please contact our Data Protection Officer at privacy@intasida.com</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
